import { Box, Divider, Typography, CircularProgress, Avatar } from '@mui/material';
import { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import zeplinColor from '../../../../../../../../theme/Color';
import { DealsService } from '../../../../../../../../service/deals.service';
import styles from './DealHistoryRecord.module.css';

interface Change {
  field: string;
  newValue: any;
  oldValue: any;
}

interface Actor {
  name: string;
  email: string;
  id: string;
}

interface ActivityRecord {
  activityId: string;
  action: string;
  dealId: string;
  accountId: string;
  actor: Actor;
  changes: Change[];
  meta: {
    pipelineType?: string;
    dealName?: string;
    [key: string]: any;
  };
  createdAt: string;
}

interface DealActivityResponse {
  success: boolean;
  total: number;
  offset: number;
  limit: number;
  records: ActivityRecord[];
}

const getInitials = (name?: string) => {
  if (!name || typeof name !== 'string') return '';
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
};

const formatAction = (action: string) => {
  return action
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

const formatFieldName = (field: string) => {
  return field
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (str) => str.toUpperCase())
    .trim();
};

const formatValue = (value: any): string => {
  if (value === null || value === undefined) return 'N/A';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'number') return value.toString();
  return String(value);
};

export const DealHistoryRecord = ({ dealId }: { dealId: string }) => {
  const [activityData, setActivityData] = useState<DealActivityResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchActivity = async () => {
      if (!dealId) return;
      
      setLoading(true);
      setError(null);
      try {
        const response = await DealsService.getDealActivity(dealId, 20, 0);
        setActivityData(response);
      } catch (err) {
        console.error('Failed to fetch deal activity:', err);
        setError('Failed to load activity history');
      } finally {
        setLoading(false);
      }
    };

    fetchActivity();
  }, [dealId]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ py: 2 }}>
        <Typography variant='body2' color='error'>
          {error}
        </Typography>
      </Box>
    );
  }

  if (!activityData || !activityData.records || activityData.records.length === 0) {
    return (
      <Box sx={{ py: 2 }}>
        <Typography variant='body2' sx={{ color: zeplinColor.Neutral9 }}>
          No activity history available.
        </Typography>
      </Box>
    );
  }

  return (
    <Box className={styles['timeline-items']}>
      {activityData.records.map((record, index) => {
        const userName = record.actor?.name || 'Unknown User';
        const formattedDate = record.createdAt
          ? dayjs(record.createdAt).format('D MMMM, YYYY')
          : 'Unknown Date';
        const actionText = formatAction(record.action || 'UNKNOWN_ACTION');

        return (
          <Box
            key={record.activityId || index}
            className={styles['timeline-item']}
            sx={{
              position: 'relative',
              backgroundColor: zeplinColor.Neutral3,
              padding: '16px',
              borderRadius: '14px',
              transform: 'translateY(-20px)',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                marginBottom: '12px',
              }}
            >
              <Typography variant='body3'>{formattedDate}</Typography>
              <Divider orientation='vertical' flexItem sx={{ color: zeplinColor.BasicBlack }} />
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Avatar
                  sx={{
                    width: '24px',
                    height: '24px',
                    fontSize: '10px',
                    marginRight: '8px',
                    backgroundColor: zeplinColor.Brand1,
                  }}
                >
                  {getInitials(userName)}
                </Avatar>
                {userName}
              </Box>
            </Box>
            <Typography variant='body2' sx={{ marginBottom: record.changes && record.changes.length > 0 ? '8px' : 0 }}>
              {actionText}
            </Typography>
            {record.changes && record.changes.length > 0 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px', mt: 1 }}>
                {record.changes.map((change, changeIndex) => (
                  <Box key={changeIndex} sx={{ pl: 1 }}>
                    <Typography variant='body3' sx={{ color: zeplinColor.Neutral8 }}>
                      <strong>{formatFieldName(change.field)}</strong>: {formatValue(change.oldValue)} → {formatValue(change.newValue)}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        );
      })}
    </Box>
  );
};
